import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaPlus } from 'react-icons/fa6';
import * as adminResponsibilitiesService from '../../data/services/adminResponsibilitiesService';
import * as adminFrameworksService from '../../data/services/adminFrameworksService';
import PromptSelector from './PromptSelector';
import './ResponsibilityItem.css';

const ResponsibilityItem = ({ responsibility, roleId, onUpdate, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPromptSelector, setShowPromptSelector] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);
  const [formData, setFormData] = useState({
    responsibilityName: responsibility.responsibilityName || '',
    definition: responsibility.definition || '',
  });

  const loadFrameworks = async () => {
    if (!isExpanded && !showPromptSelector) return;
    
    setLoadingFrameworks(true);
    try {
      const data = await adminFrameworksService.getFrameworksByResponsibilityId(responsibility.responsibilityId);
      setFrameworks(data);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    } finally {
      setLoadingFrameworks(false);
    }
  };

  React.useEffect(() => {
    if (isExpanded || showPromptSelector) {
      loadFrameworks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, showPromptSelector]);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleSave = async () => {
    try {
      const updated = await adminResponsibilitiesService.updateResponsibility(
        responsibility.responsibilityId,
        {
          responsibilityName: formData.responsibilityName.trim(),
          definition: formData.definition.trim() || null,
        }
      );
      if (onUpdate) {
        onUpdate(updated);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating responsibility:', error);
      alert(error.message || 'Failed to update responsibility');
    }
  };

  const handleCancel = () => {
    setFormData({
      responsibilityName: responsibility.responsibilityName || '',
      definition: responsibility.definition || '',
    });
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!window.confirm(`Are you sure you want to delete "${responsibility.responsibilityName}"?`)) {
      return;
    }
    
    if (onDelete) {
      onDelete(responsibility.responsibilityId);
    }
  };

  const handleRemoveFramework = async (promptId, isGlobalPrompt) => {
    try {
      await adminFrameworksService.removeFramework(
        responsibility.responsibilityId,
        promptId,
        isGlobalPrompt
      );
      await loadFrameworks();
    } catch (error) {
      console.error('Error removing framework:', error);
      alert(error.message || 'Failed to remove prompt');
    }
  };

  const handleAddPrompts = async (selectedPrompts) => {
    try {
      const promptIds = selectedPrompts.map(p => p.promptId || p.id);
      // Determine if each prompt is global or org-specific
      const isGlobalPrompts = selectedPrompts.map(p => p.isGlobal === true || p.isGlobal === 1 || p.isGlobal === '1' || p.isGlobal === 'true');
      
      await adminFrameworksService.assignFrameworks(
        responsibility.responsibilityId,
        promptIds,
        isGlobalPrompts
      );
      
      await loadFrameworks();
      setShowPromptSelector(false);
    } catch (error) {
      console.error('Error adding prompts:', error);
      alert(error.message || 'Failed to add prompts');
    }
  };

  return (
    <div className="responsibility-item">
      <div className="responsibility-item-header">
        <div className="responsibility-item-main" onClick={handleToggleExpand}>
          <div className="responsibility-item-title-section">
            {isExpanded ? (
              <FaChevronUp className="expand-icon" />
            ) : (
              <FaChevronDown className="expand-icon" />
            )}
            <span className="responsibility-item-name">
              {isEditing ? (
                <input
                  type="text"
                  className="responsibility-edit-input"
                  value={formData.responsibilityName}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsibilityName: e.target.value }))}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                responsibility.responsibilityName
              )}
            </span>
          </div>
          <div className="responsibility-item-actions">
            {isEditing ? (
              <>
                <button
                  className="save-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSave();
                  }}
                >
                  Save
                </button>
                <button
                  className="cancel-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  aria-label="Delete responsibility"
                >
                  Delete
                </button>
                <button
                  className="edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="responsibility-item-content">
          {isEditing ? (
            <div className="responsibility-edit-form">
              <label className="form-label">Definition</label>
              <textarea
                className="responsibility-edit-textarea"
                value={formData.definition}
                onChange={(e) => setFormData(prev => ({ ...prev, definition: e.target.value }))}
                placeholder="Enter a description for this responsibility..."
                rows={3}
              />
            </div>
          ) : (
            <>
              {responsibility.definition && (
                <p className="responsibility-definition">{responsibility.definition}</p>
              )}
              
              <div className="responsibility-frameworks-section">
                <div className="frameworks-header">
                  <h4 className="frameworks-title">Assigned Prompts ({frameworks.length})</h4>
                  <button
                    className="add-prompts-button"
                    onClick={() => setShowPromptSelector(true)}
                  >
                    <FaPlus className="add-icon" />
                    <span>Add Prompts</span>
                  </button>
                </div>

                {loadingFrameworks ? (
                  <div className="loading-frameworks">Loading prompts...</div>
                ) : frameworks.length > 0 ? (
                  <div className="frameworks-list">
                    {frameworks.map((fw) => {
                      const isGlobal = fw.isGlobalPrompt === true || fw.isGlobalPrompt === 1 || fw.isGlobalPrompt === '1';
                      const promptTitle = fw.promptTitle || null;
                      const promptDescription = fw.promptDescription || null;
                      const orgDomain = fw.orgDomain || null;
                      
                      return (
                        <div key={`${fw.promptId}-${isGlobal}`} className="framework-item">
                          <div className="framework-info">
                            <div className="framework-title-section">
                              <span className="framework-title">
                                {promptTitle || fw.promptId}
                              </span>
                              {isGlobal ? (
                                <span className="framework-badge">Global</span>
                              ) : orgDomain ? (
                                <span className="framework-badge" style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                                  {orgDomain}
                                </span>
                              ) : null}
                            </div>
                            {promptDescription && (
                              <div className="framework-description">{promptDescription}</div>
                            )}
                          </div>
                          <button
                            className="remove-framework-button"
                            onClick={() => handleRemoveFramework(fw.promptId, isGlobal)}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-frameworks">No prompts assigned yet. Click "Add Prompts" to assign prompts to this responsibility.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {showPromptSelector && (
        <PromptSelector
          onSelect={handleAddPrompts}
          onCancel={() => setShowPromptSelector(false)}
          existingPromptIds={frameworks.map(fw => fw.promptId)}
          roleId={roleId}
        />
      )}
    </div>
  );
};

export default ResponsibilityItem;
